import { IBuildings, IGeoCities } from '@llws/lift-entity-interfaces';
import {
  GreaterThan, GreaterThanOrEqual, In, INestedWhereCommand, IWhereCommand, LessThanOrEqual,
} from '@llws/dynamic-router-utils';
import dayjs from 'dayjs';
import { decodeHtml } from './decode';
import { ISelectOption } from '../interfaces';
import { TAccessType } from '../types/t-access-type';
import app from '@/main';
import i18n from '@/i18n';

export const SELECTED_FILTERS_INIT_VALUES = {
  properties: [],
  cities: [],
  status: '',
  categories: [],
  start: '',
  end: '',
  searchValue: '',
};

export const PROMOTIONS_MEDIA_DEFAULT = {
  imageUrl: '' as string,
  pdfUrl: '' as string,
};

export const TOOLTIP_TITLES = {
  promotionCategory: 'Promotion categories will only display on applicable rental listing sites that support the promotion category filter and tag; please contact support for more details.',
  image: 'Any images you add to this promotion will appear on applicable rental listing sites and/or websites.',
  pdf: 'PDF uploads are only displayed on Rentsync websites. If you do not have one, uploading a PDF will have no effect.',
  startDate: `The Start Date determines when your promotion will appear on rental listings; toggling on "Allow advertising before start date" will advertise the promotion immediately, showing as "Valid From: [Start Date]"`,
  allowEarlyAds: 'Allowing advertising before the start date only displays on rental listing sites that support the promotion category filter and tag.',
};

/**
 * Returns translated tooltip strings for the promotion single page.
 * Uses i18n directly so it stays reactive to locale changes.
 */
export const getTooltipTitles = (): typeof TOOLTIP_TITLES => ({
  promotionCategory: i18n.t('promotions.single.tooltipCategory') as string,
  image: i18n.t('promotions.single.tooltipImage') as string,
  pdf: i18n.t('promotions.single.tooltipPdf') as string,
  startDate: TOOLTIP_TITLES.startDate,
  allowEarlyAds: TOOLTIP_TITLES.allowEarlyAds,
});

/**
 * Creates properties options
 */
export const propertiesOptions = (properties: IBuildings[]): ISelectOption[] => properties
  .map((b: IBuildings) => ({ text: decodeHtml(b.buildingName), value: b.id }));

/**
 * Creates cities options
 */
export const citiesOptions = (cities: IGeoCities[]): ISelectOption[] => cities.map((c: IGeoCities) => ({ text: c.cityName, value: c.id }))
  .sort((a: ISelectOption, b: ISelectOption) => (a.text < b.text ? -1 : 1));

/**
 * Builds a search filter query based on a search string to be used when fetching promotions.
 *
 */
export const getSearchFilter = (searchValue?: string): INestedWhereCommand[] => {
  if (!searchValue) return [];

  return [
    {
      condition: 'and',
      whereCommands: [
        { operator: 'like', condition: 'or', field: 'title', value: searchValue, notOperator: false },
        { operator: 'like', condition: 'or', field: 'promotionsCategory.title', value: searchValue, notOperator: false },
      ],
    },
  ];
};

/**
 * Builds date filter query for start and end dates to be used when fetching promotions.
 */
export const getDateFilters = (startDate?: string, endDate?: string): IWhereCommand[] => {
  const filters: IWhereCommand[] = [];

  if (startDate) {
    filters.push(GreaterThanOrEqual('start', dayjs(startDate).subtract(1, 'day').format('YYYY-MM-DD')));
  }

  if (endDate) {
    filters.push(LessThanOrEqual('end', dayjs(endDate).add(1, 'day').format('YYYY-MM-DD')));
  }

  return filters;
};

/**
 * Builds status filter query based on promotion status (enabled, expired, scheduled, disabled) to be used when fetching promotions.
 */
export const getStatusFilters = (selectedStatus: string, startDate?: string, endDate?: string): INestedWhereCommand[] => {
  const today = dayjs().format('YYYY-MM-DD');
  switch (selectedStatus) {
    case 'enabled':
      return [
        {
          condition: 'and',
          whereCommands: [
            { operator: '', condition: '', field: 'status', value: 'disabled', notOperator: true },
          ],
        },
        {
          condition: 'and',
          whereCommands: [
            { operator: 'null', condition: 'or', field: 'end', value: '', notOperator: false },
            { operator: 'gte', condition: 'or', field: 'end', value: endDate || today, notOperator: false },
            { operator: 'lte', condition: 'and', field: 'start', value: startDate || today, notOperator: false },
          ],
        },
      ];
    case 'expired':
      return [
        {
          condition: 'and',
          whereCommands: [
            { operator: '', condition: '', field: 'status', value: 'disabled', notOperator: true },
          ],
        },
        {
          condition: 'and',
          whereCommands: [
            { operator: 'lte', condition: 'or', field: 'end', value: endDate || today, notOperator: false },
          ],
        },
      ];
    case 'scheduled':
      return [
        {
          condition: 'and',
          whereCommands: [
            { operator: '', condition: '', field: 'status', value: 'disabled', notOperator: true },
          ],
        },
        {
          condition: 'and',
          whereCommands: [
            { operator: `${startDate ? 'gte' : 'gt'}`, condition: '', field: 'start', value: `${startDate ? `${startDate}` : `${today}`}`, notOperator: false },
          ],
        },
      ];
    case 'disabled':
      return [
        {
          condition: 'and',
          whereCommands: [
            { operator: '', condition: '', field: 'status', value: 'disabled', notOperator: false },
          ],
        },
      ];
    default:
      return [];
  }
};

/**
 * Builds property and city filter query based on selected properties and cities to be used when fetching promotions.
 */
export const getPropertyAndCityFilters = (selectedProperties: number[], selectedCities: number[]): IWhereCommand[] => {
  const filters: IWhereCommand[] = [];

  if (selectedProperties.length || selectedCities.length) {
    filters.push(GreaterThan('countBuildingsFiltered', '0'));
  }
  if (selectedProperties.length) {
    filters.push(In('buildings.id', selectedProperties));
  }
  if (selectedCities.length) {
    filters.push(In('buildings.cityId', selectedCities));
  }

  return filters;
};

/**
 * Builds category filter query to be used when fetching promotions.
 */
export const getCategoryFilter = (selectedCategories: number[]) => {
  const filters: IWhereCommand[] = [];

  if (selectedCategories.length) {
    filters.push(In('promotionsCategoryId', selectedCategories));
  }

  return filters;
};

/**
 * Checks for property promotions access level on current user
 */
export const hasPromotionAccessFor = (id?: IBuildings['id'], accessLevel: TAccessType = 'read') => {
  if (!id) {
    return false;
  }

  return app()?.$rsGlobals.$userHasAccess('promotions', accessLevel, id);
};

/**
 * Checks if user has the same promotions access level for all given `buildings`.
 */
export const hasPromotionsAccessForEach = (buildings: Partial<IBuildings>[], accessLevel: TAccessType = 'read') => {
  const perms = buildings.map((b) => hasPromotionAccessFor(b.id ?? undefined, accessLevel));
  return !perms.includes(false);
};

/**
 * Checks if user has at least one promo access for all given `buildings`
 */
export const hasAtLeastOnePromotionAccessFor = (buildings: Partial<IBuildings>[], accessLevel: TAccessType = 'read') => {
  if (buildings.length === 0 && app()?.$rsGlobals.$userHasAtLeastOneAccess('promotions', accessLevel)) {
    return true;
  }

  const perms = buildings.map((b) => hasPromotionAccessFor(b.id ?? undefined, accessLevel));
  return perms.includes(true);
};
